import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Shield, ArrowLeft, Search, Filter, Download, Activity, CheckCircle, AlertTriangle, Ghost, Lock, Clock, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SecuritySettings = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [securitySettings, setSecuritySettings] = useState({
    ghost_protocol_enabled: false,
    super_admin_shortcut_enabled: true,
    settings_password_enabled: false,
    enterprise_password_enabled: false,
    session_timeout_minutes: 480,
    max_login_attempts: 5,
    lockout_duration_minutes: 15,
    require_2fa: false,
    password_expiry_days: 0
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    user_id: '',
    limit: 100
  });
  const [stats, setStats] = useState({
    total_logs: 0,
    login_count: 0,
    create_count: 0,
    update_count: 0,
    delete_count: 0
  });

  useEffect(() => {
    fetchSecuritySettings();
    fetchAuditLogs();
  }, []);

  const fetchSecuritySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/security`);
      setSecuritySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch security settings:', error);
    }
  };

  const updateSecuritySetting = async (field, value) => {
    setSavingSettings(true);
    try {
      const response = await axios.patch(`${API}/settings/security`, {
        [field]: value
      });
      setSecuritySettings(prev => ({ ...prev, [field]: value }));
      toast.success(`${field === 'ghost_protocol_enabled' ? 'Ghost Protocol' : field.replace(/_/g, ' ')} ${value ? 'enabled' : typeof value === 'boolean' ? 'disabled' : 'updated'}`);
    } catch (error) {
      console.error('Failed to update security settings:', error);
      toast.error('Failed to update setting');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      params.append('limit', filters.limit);

      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      
      // Calculate stats
      const total = response.data.total;
      const loginCount = response.data.logs.filter(l => l.action === 'LOGIN').length;
      const createCount = response.data.logs.filter(l => l.action === 'CREATE').length;
      const updateCount = response.data.logs.filter(l => l.action === 'UPDATE').length;
      const deleteCount = response.data.logs.filter(l => l.action === 'DELETE').length;
      
      setStats({
        total_logs: total,
        login_count: loginCount,
        create_count: createCount,
        update_count: updateCount,
        delete_count: deleteCount
      });
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    setLoading(true);
    fetchAuditLogs();
  };

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity ID', 'IP Address'].join(','),
      ...logs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.user_email,
        log.action,
        log.entity_type,
        log.entity_id || '',
        log.ip_address || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString()}.csv`;
    a.click();
    toast.success('Audit logs exported');
  };

  const getActionBadge = (action) => {
    const variants = {
      LOGIN: { color: 'bg-blue-600', icon: Activity },
      LOGOUT: { color: 'bg-gray-600', icon: Activity },
      CREATE: { color: 'bg-green-600', icon: CheckCircle },
      UPDATE: { color: 'bg-yellow-600', icon: AlertTriangle },
      DELETE: { color: 'bg-red-600', icon: AlertTriangle }
    };
    
    const variant = variants[action] || { color: 'bg-gray-600', icon: Activity };
    const Icon = variant.icon;
    
    return (
      <Badge className={variant.color}>
        <Icon className="h-3 w-3 mr-1" />
        {action}
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Security & Audit</h1>
              <p className="text-muted-foreground">Monitor system activity and security events</p>
            </div>
          </div>
        </div>

        {/* Security Settings Card */}
        <Card className="border-2 border-purple-200 dark:border-purple-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-500" />
              Security Configuration
            </CardTitle>
            <CardDescription>Configure authentication and security policies</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Super Admin Shortcut Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                  <Key className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <Label className="text-base font-semibold">Super Admin Quick Login</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    When enabled, clicking the Sign In button 3 times will auto-login as Super Admin (no password required).
                  </p>
                </div>
              </div>
              <Switch
                checked={securitySettings.super_admin_shortcut_enabled ?? true}
                onCheckedChange={(checked) => updateSecuritySetting('super_admin_shortcut_enabled', checked)}
                disabled={savingSettings}
              />
            </div>

            {/* Ghost Protocol Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Ghost className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <Label className="text-base font-semibold">Ghost Protocol Password</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    When enabled, clicking the logo 3 times shows Ghost Protocol access dialog.
                  </p>
                </div>
              </div>
              <Switch
                checked={securitySettings.ghost_protocol_enabled}
                onCheckedChange={(checked) => updateSecuritySetting('ghost_protocol_enabled', checked)}
                disabled={savingSettings}
              />
            </div>

            {/* Settings Password Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <Shield className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <Label className="text-base font-semibold">Settings Password Protection</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    When enabled, require password to open the Settings page.
                  </p>
                </div>
              </div>
              <Switch
                checked={securitySettings.settings_password_enabled ?? false}
                onCheckedChange={(checked) => updateSecuritySetting('settings_password_enabled', checked)}
                disabled={savingSettings}
              />
            </div>

            {/* Enterprise Password Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <Label className="text-base font-semibold">Rhino-1 Enterprise Password Protection</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    When enabled, require password to open the Rhino-1 Enterprise tab.
                  </p>
                </div>
              </div>
              <Switch
                checked={securitySettings.enterprise_password_enabled ?? false}
                onCheckedChange={(checked) => updateSecuritySetting('enterprise_password_enabled', checked)}
                disabled={savingSettings}
              />
            </div>

            {/* Session Timeout */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <Label className="text-base font-semibold">Session Timeout</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Automatically log out users after this period of inactivity.
                  </p>
                </div>
              </div>
              <Select 
                value={securitySettings.session_timeout_minutes?.toString()} 
                onValueChange={(value) => updateSecuritySetting('session_timeout_minutes', parseInt(value))}
                disabled={savingSettings}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="240">4 hours</SelectItem>
                  <SelectItem value="480">8 hours</SelectItem>
                  <SelectItem value="1440">24 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Max Login Attempts */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <Lock className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <Label className="text-base font-semibold">Max Login Attempts</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Lock account after this many failed login attempts.
                  </p>
                </div>
              </div>
              <Select 
                value={securitySettings.max_login_attempts?.toString()} 
                onValueChange={(value) => updateSecuritySetting('max_login_attempts', parseInt(value))}
                disabled={savingSettings}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 attempts</SelectItem>
                  <SelectItem value="5">5 attempts</SelectItem>
                  <SelectItem value="10">10 attempts</SelectItem>
                  <SelectItem value="0">Unlimited</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Two-Factor Authentication */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Key className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <Label className="text-base font-semibold">Require Two-Factor Authentication</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Require all users to set up 2FA for their accounts.
                  </p>
                </div>
              </div>
              <Switch
                checked={securitySettings.require_2fa}
                onCheckedChange={(checked) => updateSecuritySetting('require_2fa', checked)}
                disabled={savingSettings}
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_logs}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Logins</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.login_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Creates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.create_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Updates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.update_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Deletes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.delete_count}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Audit Logs</CardTitle>
                <CardDescription>View and filter system activity logs</CardDescription>
              </div>
              <Button variant="outline" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div>
                <Label>Action Type</Label>
                <Select value={filters.action || "all"} onValueChange={(value) => setFilters({...filters, action: value === "all" ? "" : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    <SelectItem value="LOGIN">Login</SelectItem>
                    <SelectItem value="LOGOUT">Logout</SelectItem>
                    <SelectItem value="CREATE">Create</SelectItem>
                    <SelectItem value="UPDATE">Update</SelectItem>
                    <SelectItem value="DELETE">Delete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Entity Type</Label>
                <Select value={filters.entity_type || "all"} onValueChange={(value) => setFilters({...filters, entity_type: value === "all" ? "" : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All entities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All entities</SelectItem>
                    <SelectItem value="User">User</SelectItem>
                    <SelectItem value="Team">Team</SelectItem>
                    <SelectItem value="CompanySettings">Company Settings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Limit</Label>
                <Select value={filters.limit.toString()} onValueChange={(value) => setFilters({...filters, limit: parseInt(value)})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50 records</SelectItem>
                    <SelectItem value="100">100 records</SelectItem>
                    <SelectItem value="250">250 records</SelectItem>
                    <SelectItem value="500">500 records</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={handleFilterChange} className="w-full">
                  <Filter className="h-4 w-4 mr-2" />
                  Apply Filters
                </Button>
              </div>
            </div>

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity Type</TableHead>
                    <TableHead>Entity ID</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Loading audit logs...
                      </TableCell>
                    </TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No audit logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-xs">
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>{log.user_email}</TableCell>
                        <TableCell>{getActionBadge(log.action)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.entity_type}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.entity_id ? log.entity_id.substring(0, 8) + '...' : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {log.ip_address || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
